import { useState } from 'react';
import { Box, Chip, InputAdornment, TextField, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import { specParam, type SpecField } from '@zilian/contracts';
import { decimalInput } from './ui';

/**
 * A category's spec parameters, one row each: the usual values are one click, and "+ 自定义"
 * opens a box for anything else (open from the start when such a value is set). Clicking the
 * chosen value again clears it. A parameter without usual values is typed straight away; a
 * numeric one (克重) takes a number only and shows its unit. Values are keyed by parameter key.
 */
export function SpecPanel(p: {
  fields: SpecField[];
  values: Record<string, string>;
  disabled?: boolean;
  onChange: (values: Record<string, string>) => void;
}) {
  const [typing, setTyping] = useState<string[]>([]);
  const set = (key: string, value: string) => p.onChange({ ...p.values, [key]: value });
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '64px minmax(0, 1fr)',
        columnGap: 2,
        rowGap: 1.5,
        alignItems: 'start',
      }}
    >
      {p.fields.map((field) => {
        const value = p.values[field.key] ?? '';
        const typed = value && !field.options.includes(value) ? value : '';
        const open = !field.options.length || !!typed || typing.includes(field.key);
        const param = specParam(field.key);
        return (
          <Box key={field.key} sx={{ display: 'contents' }}>
            <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: '28px' }}>
              {field.label}
              {field.required && (
                <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                  *
                </Box>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
              {field.options.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  size="small"
                  clickable
                  disabled={p.disabled}
                  icon={value === option ? <Check /> : undefined}
                  color={value === option ? 'primary' : 'default'}
                  variant={value === option ? 'filled' : 'outlined'}
                  onClick={() => set(field.key, value === option ? '' : option)}
                  sx={{
                    height: 28,
                    fontSize: 13,
                    minWidth: 44,
                    '& .MuiChip-icon': { fontSize: 15 },
                  }}
                />
              ))}
              {open ? (
                <TextField
                  size="small"
                  autoFocus={typing.includes(field.key) && !typed}
                  placeholder={field.options.length ? '自定义' : param?.numeric ? '如 25' : '填写'}
                  value={typed}
                  disabled={p.disabled}
                  onChange={(e) =>
                    set(
                      field.key,
                      param?.numeric ? decimalInput(e.target.value, 4) : e.target.value,
                    )
                  }
                  onBlur={() =>
                    !typed && setTyping((all) => all.filter((key) => key !== field.key))
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: 30,
                      inputMode: param?.numeric ? 'decimal' : undefined,
                      'aria-label': field.label,
                    },
                    input: {
                      endAdornment: param?.numeric && param.unit && (
                        <InputAdornment position="end">{param.unit}</InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    width: field.options.length ? 96 : 160,
                    // Chip-sized among the chips.
                    '& .MuiInputBase-root': { height: 28 },
                    '& .MuiInputBase-input': { py: 0, fontSize: 13 },
                  }}
                />
              ) : (
                <Chip
                  label="自定义"
                  size="small"
                  clickable
                  icon={<Add />}
                  disabled={p.disabled}
                  variant="outlined"
                  onClick={() => setTyping((all) => [...all, field.key])}
                  sx={{
                    height: 28,
                    fontSize: 13,
                    color: 'text.secondary',
                    borderStyle: 'dashed',
                    '& .MuiChip-icon': { fontSize: 16, color: 'inherit' },
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
